import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { 
  Users, 
  Truck, 
  Package, 
  FileText,
  Settings,
  AlertTriangle,
  BarChart3
} from 'lucide-react';

const AdministradorDashboard = () => {
  const navigate = useNavigate();
  const { user, userData, getEmpresaId } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Função auxiliar para extrair mensagem de erro específica
  const getErrorMessage = (error) => {
    console.log('🔍 Analisando erro:', error);
    
    // Verificar se é um erro do Firebase Functions
    if (error.code && error.code.startsWith('functions/')) {
      return error.message || 'Erro na validação dos dados';
    }
    
    // Verificar códigos específicos
    if (error.code === 'functions/invalid-argument') {
      return error.message || 'Dados inválidos fornecidos';
    }
    
    if (error.code === 'functions/permission-denied') {
      return 'Você não tem permissão para acessar estes dados';
    }
    
    if (error.code === 'functions/not-found') {
      return 'Dados não encontrados para esta empresa';
    }
    
    if (error.code === 'functions/unauthenticated') {
      return 'Sessão expirada. Faça login novamente';
    }
    
    // Verificar se há uma mensagem específica no erro
    if (error.message && error.message !== 'INTERNAL') {
      return error.message;
    }
    
    // Fallback para erro genérico
    return 'Erro ao conectar com o servidor';
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 AdministradorDashboard: Carregando dados reais...');

      // ✅ CORREÇÃO: Obter empresa_id do usuário logado
      const empresaId = getEmpresaId();
      console.log('🔍 AdministradorDashboard: empresa_id:', empresaId);

      // Carregar estatísticas reais da empresa
      // Documentos pendentes: usar listDocumentosParaAnaliseQualidade (documentos enviados por fornecedores aguardando aprovação)
      const [
        usersResult,
        suppliersResult,
        productsResult,
        documentosAnaliseResult
      ] = await Promise.allSettled([
        httpsCallable(functions, 'listUsers')({ page: 1, limit: 1000 }),
        httpsCallable(functions, 'getSupplierStats')({ empresa_id: empresaId }),
        httpsCallable(functions, 'getProductStats')({ empresa_id: empresaId }),
        empresaId ? httpsCallable(functions, 'listDocumentosParaAnaliseQualidade')({ empresa_id: empresaId, page: 1, limit: 1 }) : Promise.reject(new Error('Sem empresa'))
      ]);

      // Documentos aguardando aprovação = em_analise + pendentes (documentos_enviados)
      const statsDocs = documentosAnaliseResult.status === 'fulfilled' ? documentosAnaliseResult.value.data?.data?.stats : null;
      const documentosPendentes = statsDocs
        ? (statsDocs.em_analise || 0) + (statsDocs.pendentes || 0)
        : 0;

      // Processar resultados e calcular estatísticas
      const realStats = {
        // Usuários
        total_usuarios: usersResult.status === 'fulfilled' ? 
          usersResult.value.data?.data?.pagination?.total || 0 : 0,
        usuarios_ativos: usersResult.status === 'fulfilled' ? 
          usersResult.value.data?.data?.items?.filter(u => u.ativo === true).length || 0 : 0,
        usuarios_pendentes: usersResult.status === 'fulfilled' ? 
          usersResult.value.data?.data?.items?.filter(u => u.ativo === false).length || 0 : 0,

        // Fornecedores
        total_fornecedores: suppliersResult.status === 'fulfilled' ? 
          suppliersResult.value.data?.data?.total || 0 : 0,
        fornecedores_ativos: suppliersResult.status === 'fulfilled' ? 
          suppliersResult.value.data?.data?.por_status?.aprovado || 0 : 0,
        fornecedores_pendentes: suppliersResult.status === 'fulfilled' ? 
          suppliersResult.value.data?.data?.por_status?.pendente || 0 : 0,

        // Produtos
        total_produtos: productsResult.status === 'fulfilled' ? 
          productsResult.value.data?.data?.total || 0 : 0,

        // Documentos (enviados por fornecedores aguardando aprovação)
        documentos_pendentes: documentosPendentes,

        // Sistema
        configuracoes_pendentes: 0,
        backup_ultimo: new Date().toISOString().split('T')[0],
        sistema_saude: 'ok'
      };

      setStats(realStats);

      console.log('✅ AdministradorDashboard: Dados reais carregados:', realStats);

    } catch (error) {
      console.error('❌ Erro ao carregar dados do dashboard:', error);
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      
      // Em caso de erro, usar dados zerados
      setStats({
        total_usuarios: 0,
        usuarios_ativos: 0,
        usuarios_pendentes: 0,
        total_fornecedores: 0,
        fornecedores_ativos: 0,
        fornecedores_pendentes: 0,
        total_produtos: 0,
        documentos_pendentes: 0,
        configuracoes_pendentes: 0,
        backup_ultimo: 'N/A',
        sistema_saude: 'ok'
      });
    } finally {
      setLoading(false);
    }
  };

  // ✅ DADOS MOCKADOS REMOVIDOS - AGORA USAMOS DADOS REAIS

  const currentStats = stats || {
    total_usuarios: 0,
    usuarios_ativos: 0,
    usuarios_pendentes: 0,
    total_fornecedores: 0,
    fornecedores_ativos: 0,
    fornecedores_pendentes: 0,
    total_produtos: 0,
    documentos_pendentes: 0,
    configuracoes_pendentes: 0,
    backup_ultimo: 'N/A',
    sistema_saude: 'ok'
  };

  const mainCards = [
    {
      title: 'Usuários da Empresa',
      value: currentStats.total_usuarios,
      subtitle: `${currentStats.usuarios_ativos} ativos, ${currentStats.usuarios_pendentes} pendentes`,
      icon: Users,
      color: 'bg-blue-500',
      trend: '+2 este mês'
    },
    {
      title: 'Fornecedores',
      value: currentStats.total_fornecedores,
      subtitle: `${currentStats.fornecedores_ativos} ativos`,
      icon: Truck,
      color: 'bg-green-500',
      trend: '+1 este mês'
    },
    {
      title: 'Ingredientes Cadastrados',
      value: currentStats.total_produtos,
      subtitle: 'Na empresa',
      icon: Package,
      color: 'bg-purple-500',
      trend: '+8 este mês'
    },
    {
      title: 'Documentos Pendentes',
      value: currentStats.documentos_pendentes,
      subtitle: 'Aguardando aprovação (enviados por fornecedores)',
      icon: FileText,
      color: 'bg-orange-500',
      trend: 'Em análise pela qualidade'
    }
  ];

  // ✅ REMOVIDO: systemCards (Saúde do Sistema, Último Backup, Configurações)

  // ✅ REMOVIDO: Funções auxiliares não mais utilizadas (getRoleDisplayName, formatTimeAgo, getActivityIcon, getActivityColor)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2" />
          {error}
          <button 
            onClick={loadDashboardData}
            className="ml-auto text-red-700 hover:text-red-900 underline text-sm"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">
          Bem-vindo, {userData?.nome || user?.displayName || 'Administrador'}
        </h1>
        <p className="text-blue-100">
          Painel administrativo - Gestão de usuários, fornecedores e configurações do sistema
        </p>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {mainCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{card.title}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{card.value}</p>
                  <p className="text-sm text-gray-500 mt-1">{card.subtitle}</p>
                </div>
                <div className={`${card.color} p-3 rounded-lg`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm text-green-600 font-medium">{card.trend}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Actions and System Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Ações Administrativas</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <button 
                onClick={() => navigate('/app/users')}
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
              >
                <Users className="h-6 w-6 text-blue-600 mb-2" />
                <p className="text-sm font-medium text-gray-900">Gerenciar Usuários</p>
                <p className="text-xs text-gray-500">Aprovar/rejeitar usuários</p>
              </button>
              <button 
                onClick={() => navigate('/app/fornecedores/ingredientes')}
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
              >
                <Truck className="h-6 w-6 text-green-600 mb-2" />
                <p className="text-sm font-medium text-gray-900">Fornecedores</p>
                <p className="text-xs text-gray-500">Gerenciar fornecedores</p>
              </button>
              <button 
                onClick={() => navigate('/app/settings')}
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
              >
                <Settings className="h-6 w-6 text-purple-600 mb-2" />
                <p className="text-sm font-medium text-gray-900">Configurações</p>
                <p className="text-xs text-gray-500">Sistema e empresa</p>
              </button>
              <button 
                onClick={() => {
                  alert('Relatórios estão em construção. Em breve você terá acesso a análises e métricas detalhadas.');
                }}
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
              >
                <BarChart3 className="h-6 w-6 text-orange-600 mb-2" />
                <p className="text-sm font-medium text-gray-900">Relatórios</p>
                <p className="text-xs text-gray-500">Análises e métricas</p>
              </button>
            </div>
          </div>
        </div>

        {/* System Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Informações do Sistema</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Versão do Sistema</span>
                <span className="text-sm font-medium text-gray-900">v2.1.0</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Última Atualização</span>
                <span className="text-sm font-medium text-gray-900">21/12/2024</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Uptime</span>
                <span className="text-sm font-medium text-green-600">99.9%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Usuários Online</span>
                <span className="text-sm font-medium text-gray-900">{currentStats.usuarios_ativos}</span>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-200">
              <button 
                onClick={() => navigate('/app/settings')}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Configurações Avançadas
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdministradorDashboard;

